import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  BillingCadence,
  ListingDraft,
  PublicationMode,
  PublicationOutcome,
  PublicationRequestResult,
  PublishedListing,
  SaasDemoState,
} from "../types/saas";
import { logActivity } from "../core/activityLogger";

const STORAGE_KEY = "picasso-saas-demo";
class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

const api = async (path: string, init: RequestInit = {}) => {
  const response = await fetch(`/api/demo${path}`, { ...init, credentials: "include", headers: { "Content-Type": "application/json", ...(init.headers || {}) } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, body.error || `Demo API request failed (${response.status})`);
  return body;
};

const emptyDraft = (): ListingDraft => ({
  sku: "",
  title: "",
  brand: "",
  category: "",
  price: "",
  quantity: "",
  fulfilment: "fba",
  description: "",
  selectedImageCount: 0,
});

export const createDefaultSaasDemoState = (): SaasDemoState => ({
  session: null,
  subscription: {
    planName: "Launch configuration",
    cadence: "monthly",
    includedCredits: 0,
    usedCredits: 0,
    includedMarkets: 1,
    includedChannels: 1,
    extraMarkets: 0,
    extraChannels: 0,
  },
  amazonJapan: { status: "disconnected" },
  draft: emptyDraft(),
  listings: [],
  pendingDirectPublication: false,
});

// Authentication and account data are always restored from the server.
async function loadAuthenticatedState(): Promise<SaasDemoState> {
  const body = await api("/session");
  if (!body.user?.id || !body.user?.email || !body.account?.subscription) throw new Error("Account unavailable");
  const { account, user } = body;
  const listings = await api("/listings");
  const connection = account.connections?.["amazon-jp"];
  return {
    ...createDefaultSaasDemoState(),
    session: { email: user.email, displayName: user.email.split("@")[0] },
    subscription: {
      ...createDefaultSaasDemoState().subscription,
      planName: account.subscription.plan,
      cadence: account.subscription.cadence,
      includedCredits: account.subscription.credits,
      usedCredits: account.subscription.usedCredits,
      includedMarkets: account.subscription.markets?.length ?? 0,
      includedChannels: account.subscription.channels?.length ?? 0,
    },
    amazonJapan: connection ? { status: "connected", sellerName: connection.sellerName, connectedAt: connection.authorizedAt } : { status: "disconnected" },
    listings: listings.listings || [],
  };
}

const isCompleteDraft = (draft: ListingDraft) =>
  Boolean(draft.sku.trim() && draft.title.trim() && draft.brand.trim() && draft.category.trim() && draft.price.trim() && draft.quantity.trim() && draft.description.trim());

const listingReference = () => `PIC-JP-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

export function useSaasDemo() {
  const [state, setState] = useState<SaasDemoState>(createDefaultSaasDemoState);
  const authVersion = useRef(0);

  useEffect(() => {
    // Discard legacy cached sessions, including data belonging to another user.
    window.localStorage.removeItem(STORAGE_KEY);
    const version = ++authVersion.current;
    loadAuthenticatedState().then((restored) => {
      if (authVersion.current === version) setState(restored);
    }).catch(() => {
      if (authVersion.current === version) setState(createDefaultSaasDemoState());
    });
    return () => { authVersion.current++; };
  }, []);

  const startSession = useCallback(async (identifier: string, password: string) => {
    const version = ++authVersion.current;
    setState(createDefaultSaasDemoState());
    try {
      await api("/login", { method: "POST", body: JSON.stringify({ identifier: identifier.trim(), password }) });
      const authenticated = await loadAuthenticatedState();
      if (authVersion.current !== version) throw new Error("Login cancelled");
      setState(authenticated);
      void logActivity('workspace', 'user_logged_in', { email: authenticated.session!.email });
    } catch (error) {
      if (authVersion.current === version) setState(createDefaultSaasDemoState());
      throw new Error(error instanceof ApiError && [400, 401].includes(error.status) ? "loginInvalid" : "loginUnavailable");
    }
  }, []);

  const signOut = useCallback(async () => {
    authVersion.current++;
    setState(createDefaultSaasDemoState());
    try { await api("/logout", { method: "POST" }); } catch { /* Already signed out locally. */ }
  }, []);

  const connectAmazonJapan = useCallback(async () => {
    try { const body = await api("/connection/amazon-jp", { method: "POST", body: "{}" }); const c = body.connection; setState((current) => ({ ...current, amazonJapan: { status: "connected", sellerName: c.sellerName, connectedAt: c.authorizedAt } })); return; } catch { /* local fallback */ }
    setState((current) => ({
      ...current,
      amazonJapan: {
        status: "connected",
        sellerName: "Aster Commerce JP",
        connectedAt: new Date().toISOString(),
      },
    }));
  }, []);

  const updateDraft = useCallback((patch: Partial<ListingDraft>) => {
    setState((current) => ({ ...current, draft: { ...current.draft, ...patch } }));
  }, []);

  const updateBilling = useCallback(async (patch: Partial<SaasDemoState["subscription"]>) => {
    try { const body = await api("/billing", { method: "PATCH", body: JSON.stringify({ cadence: patch.cadence }) }); if (body.subscription) patch = { ...patch, cadence: body.subscription.cadence }; } catch { /* local fallback */ }
    setState((current) => ({ ...current, subscription: { ...current.subscription, ...patch } }));
  }, []);

  const setCadence = useCallback((cadence: BillingCadence) => updateBilling({ cadence }), [updateBilling]);

  const consumeVisualCredits = useCallback(async (amount: number) => {
    try { const body = await api("/usage", { method: "POST", body: JSON.stringify({ credits: amount }) }); if (typeof body.usedCredits === "number") { void logActivity('workspace', 'credits_consumed', { amount, remaining: body.remainingCredits }); setState((current) => ({ ...current, subscription: { ...current.subscription, usedCredits: body.usedCredits } })); return; } } catch { /* local fallback */ }
    setState((current) => ({
      ...current,
      subscription: { ...current.subscription, usedCredits: Math.max(0, current.subscription.usedCredits + amount) },
    }));
  }, []);

  const createListing = useCallback((current: SaasDemoState, outcome: PublicationOutcome): PublishedListing => {
    const now = new Date().toISOString();
    const modeLabel = outcome === "draft" ? "Simulated Seller Central draft saved locally" : "Simulated Amazon Japan publish saved locally";
    return {
      ...current.draft,
      id: crypto.randomUUID?.() ?? `listing-${Date.now()}`,
      reference: listingReference(),
      outcome,
      createdAt: now,
      events: [
        { id: "validated", label: "Picasso pre-flight checks completed", createdAt: now },
        { id: outcome, label: modeLabel, createdAt: now },
      ],
    };
  }, []);

  const requestPublication = useCallback(async (mode: PublicationMode): Promise<PublicationRequestResult> => {
    if (state.amazonJapan.status !== "connected" || !isCompleteDraft(state.draft)) return { kind: "invalid" };
    if (mode === "direct") {
      setState((current) => ({ ...current, pendingDirectPublication: true }));
      return { kind: "confirmation-required" };
    }
    try {
      const body = await api("/listings", { method: "POST", body: JSON.stringify({ ...state.draft, publishMode: "draft", confirmed: false }) });
      if (body.listing) setState((current) => ({ ...current, listings: [body.listing, ...current.listings], pendingDirectPublication: false }));
    } catch { setState((current) => {
      const listing = createListing(current, "draft");
      return { ...current, listings: [listing, ...current.listings], pendingDirectPublication: false };
    }); }
    return { kind: "created", outcome: "draft" };
  }, [createListing, state.amazonJapan.status, state.draft]);

  const confirmDirectPublication = useCallback(async () => {
    try { const body = await api("/listings", { method: "POST", body: JSON.stringify({ ...state.draft, publishMode: "direct", confirmed: true }) }); if (body.listing) { setState((current) => ({ ...current, listings: [body.listing, ...current.listings], pendingDirectPublication: false })); return; } } catch { /* local fallback */ }
    setState((current) => {
      if (!current.pendingDirectPublication || current.amazonJapan.status !== "connected" || !isCompleteDraft(current.draft)) return current;
      const listing = createListing(current, "published");
      return { ...current, listings: [listing, ...current.listings], pendingDirectPublication: false };
    });
  }, [createListing]);

  const cancelDirectPublication = useCallback(() => setState((current) => ({ ...current, pendingDirectPublication: false })), []);

  return useMemo(() => ({
    state,
    startSession,
    signOut,
    connectAmazonJapan,
    updateDraft,
    updateBilling,
    setCadence,
    consumeVisualCredits,
    requestPublication,
    confirmDirectPublication,
    cancelDirectPublication,
  }), [cancelDirectPublication, confirmDirectPublication, connectAmazonJapan, consumeVisualCredits, requestPublication, setCadence, signOut, startSession, state, updateBilling, updateDraft]);
}
